<template>
  <div class="work-hours" v-if="companyConfigurationIsAvailable">
    <field
      v-for="(day, index) of weekDays"
      :class="{ 'work-hours-day': true, 'is-hidden': dayIsHidden(index) }"
      :key="'week-day-' + index">
      <label slot="label" for="">
        <template v-if="useCommonDay">{{ $t('common.workHours.workingHours') }}</template>
        <template v-else>{{ day }}:</template>
      </label>

      <switcher
        :disabled="!(!useCompanyHours || (useCompanyHours && dayInitiallyActive(index)))"
        v-model="days[index]"
        :key="'days-switcher-' + index"
        slot="field" />

      <em
        v-if="!dayIsActive(index)"
        class="label-closed"
        slot="field">
        {{ $t('common.ui.closed') }}
      </em>

      <div
        v-if="dayIsActive(index)"
        class="week-day-hours"
        :key="componentIdForRefresh + '-' + index"
        slot="field">
        <div class="week-day-hours-item">
          <select
            @change="refresh"
            v-model="daysHours[index][0][0][0]">
            <option
              v-for="(hour, hourIndex) of getAvailableHours(index, 0, 0)"
              :key="'hitem-0-' + index + '-' + hourIndex"
              :value="hour">
              {{ hour }}
            </option>
          </select>

          <select
            @change="refresh"
            v-model="daysHours[index][0][0][1]">
            <option
              v-for="(minute, minuteIndex) of getAvailableMinutes(index, 0, 0)"
              :key="'mitem-0-' + index + '-' + minuteIndex"
              :value="minute">
              {{ minute }}
            </option>
          </select>

          <span class="separator">&ndash;</span>

          <select
            @change="refresh"
            v-model="daysHours[index][0][1][0]">
            <option
              v-for="(hour, hourIndex) of getAvailableHours(index, 0, 1)"
              :key="'hitem-1-' + index + '-' + hourIndex"
              :value="hour">
              {{ hour }}
            </option>
          </select>

          <select
            @change="refresh"
            v-model="daysHours[index][0][1][1]">
            <option
              v-for="(minute, minuteIndex) of getAvailableMinutes(index, 0, 1)"
              :key="'mitem-1-' + index + '-' + minuteIndex"
              :value="minute">
              {{ minute }}
            </option>
          </select>

          <btn
            type="danger medium icon"
            iconName="cross"
            iconSize="xxs"
            iconColor="color-light"
            :disabled="!additionalHours(index).length"
            @click.native="removeHours(index, 0)"
            :title="$t('common.ui.delete')">
          </btn>

          <btn
            v-if="!additionalHours(index).length"
            type="success medium icon"
            iconName="plus"
            iconSize="xxs"
            iconColor="color-light"
            @click.native="addHours(index)"
            :title="$t('common.ui.add')">
          </btn>
        </div>

        <div
          v-for="(hours, hindex) of additionalHours(index)"
          class="week-day-hours-item"
          :key="'hitem-' + index + '-' + hindex">
          <select
            @change="refresh"
            v-model="daysHours[index][hindex + 1][0][0]">
            <option
              v-for="(hour, hourIndex) of getAvailableHours(index, hindex + 1, 0)"
              :key="'hitem-0-' + hindex + '-' + index + '-' + hourIndex"
              :value="hour">
              {{ hour }}
            </option>
          </select>

          <select
            @change="refresh"
            v-model="daysHours[index][hindex + 1][0][1]">
            <option
              v-for="(minute, minuteIndex) of getAvailableMinutes(index, hindex + 1, 0)"
              :key="'mitem-0-' + hindex + '-' + index + '-' + minuteIndex"
              :value="minute">
              {{ minute }}
            </option>
          </select>

          <span class="separator">&ndash;</span>

          <select
            @change="refresh"
            v-model="daysHours[index][hindex + 1][1][0]">
            <option
              v-for="(hour, hourIndex) of getAvailableHours(index, hindex + 1, 1)"
              :key="'hitem-1-' + hindex + '-' + index + '-' + hourIndex"
              :value="hour">
              {{ hour }}
            </option>
          </select>

          <select
            @change="refresh"
            v-model="daysHours[index][hindex + 1][1][1]">
            <option
              v-for="(minute, minuteIndex) of getAvailableMinutes(index, hindex + 1, 1)"
              :key="'mitem-1-' + hindex + '-' + index + '-' + minuteIndex"
              :value="minute">
              {{ minute }}
            </option>
          </select>

          <btn
            type="danger medium icon"
            iconName="cross"
            iconSize="xxs"
            iconColor="color-light"
            @click.native="removeHours(index, hindex + 1)"
            :title="$t('common.ui.delete')">
          </btn>

          <btn
            v-if="(hindex + 1) === additionalHours(index).length"
            type="success medium icon"
            iconName="plus"
            iconSize="xxs"
            iconColor="color-light"
            @click.native="addHours(index)"
            :title="$t('common.ui.add')">
          </btn>
        </div>
      </div>
    </field>
  </div>
</template>

<script>
import WorkHoursUtils from './../helpers/WorkHoursUtils';

export default {
  name: 'work-hours',
  props: {
    initialDays: {
      type: Array
    },
    initialDaysHours: {
      type: Array
    },
    hiddenDays: {
      type: Array,
      default: () => [false, false, false, false, false, false, false]
    },
    daysDisabledAtStart: {
      type: Boolean
    },
    useCommonDay: {
      type: Boolean,
      default: false
    },
    useCompanyHours: {
      type: Boolean
    }
  },
  data () {
    return {
      companyConfigurationIsAvailable: false,
      componentIdForRefresh: 'work-hours-' + new Date().getTime(),
      days: [false, false, false, false, false, false, false],
      daysHours: []
    };
  },
  watch: {
    initialDays (newValue) {
      this.days = JSON.parse(JSON.stringify(newValue));
    },
    initialDaysHours (newValue) {
      if (this.$store.state.config.user.id && this.useCompanyHours && !newValue.length) {
        return;
      }

      if (typeof newValue[0][0][0] === 'string') {
        this.daysHours = this.prepareHours(newValue);
      }
    },
    daysHours (newValue) {
      this.fixHours(newValue);
    }
  },
  computed: {
    weekDays () {
      return WorkHoursUtils.getDayNames();
    },
    fullDayHoursAndMinutes () {
      if (this.useCompanyHours) {
        return WorkHoursUtils.getHoursAndMinutesWithRanges(this.$store.state.config.company);
      }

      return WorkHoursUtils.getHoursAndMinutes();
    },
    companyDays () {
      return WorkHoursUtils.getCompanyWorkingDays(this.$store.state.config.company);
    }
  },
  mounted () {
    if (this.$store.state.config.user.id || !this.useCompanyHours) {
      this.activateComponent();
    }

    this.$bus.$on('configuration-loaded', this.activateComponent);
  },
  methods: {
    activateComponent () {
      this.companyConfigurationIsAvailable = true;

      setTimeout(() => {
        if (this.daysDisabledAtStart && !this.initialDays.filter(item => item).length) {
          this.days = [false, false, false, false, false, false, false];
        } else {
          this.days = this.initialDays.slice();
        }

        if (this.initialDaysHours.length) {
          if (typeof this.initialDaysHours[0][0][0] === 'string') {
            this.daysHours = this.prepareHours(this.initialDaysHours.slice());
          } else {
            this.daysHours = this.initialDaysHours.slice();
          }
        } else {
          if (this.useCompanyHours) {
            this.daysHours = WorkHoursUtils.getCompanyHours(this.$store.state.config.company);
          } else {
            this.daysHours = WorkHoursUtils.getCompanyEmptyHours();
          }
        }
      }, 0);
    },
    dayIsActive (index) {
      return this.days[index];
    },
    dayInitiallyActive (index) {
      if (this.useCompanyHours) {
        return this.companyDays[index];
      }

      return this.initialDays[index];
    },
    dayIsHidden (index) {
      if (this.hiddenDays[index]) {
        return true;
      }

      return false;
    },
    getAvailableHours (dayIndex, rangeIndex, colIndex) {
      let baseHours;

      if (this.useCompanyHours) {
        baseHours = this.fullDayHoursAndMinutes[dayIndex];
      } else {
        baseHours = this.fullDayHoursAndMinutes;
      }

      if (rangeIndex === 0 && colIndex === 0) {
        return baseHours.map(data => data.hour);
      }

      if (rangeIndex === 0 && colIndex === 1) {
        return baseHours.filter(data => data.hour >= this.daysHours[dayIndex][0][0][0])
          .map(data => data.hour);
      }

      if (rangeIndex > 0 && colIndex === 0) {
        return baseHours.filter(data => data.hour >= this.daysHours[dayIndex][rangeIndex - 1][1][0])
          .map(data => data.hour);
      }

      if (rangeIndex > 0 && colIndex === 1) {
        return baseHours.filter(data => data.hour >= this.daysHours[dayIndex][rangeIndex][0][0])
          .map(data => data.hour);
      }
    },
    getAvailableMinutes (dayIndex, rangeIndex, colIndex) {
      let baseHours;

      if (this.useCompanyHours) {
        baseHours = this.fullDayHoursAndMinutes[dayIndex];
      } else {
        baseHours = this.fullDayHoursAndMinutes;
      }

      if (rangeIndex === 0 && colIndex === 0) {
        if (this.daysHours[dayIndex][0][0][0] === '24') {
          return ['00'];
        }

        return baseHours.filter(data => data.hour === this.daysHours[dayIndex][0][0][0])
          .map(data => data.minutes)[0];
      }

      if (rangeIndex === 0 && colIndex === 1) {
        if (this.daysHours[dayIndex][0][1][0] === '24') {
          return ['00'];
        }

        if (this.daysHours[dayIndex][0][0][0] === this.daysHours[dayIndex][0][1][0]) {
          let result = baseHours.filter(data => data.hour === this.daysHours[dayIndex][0][1][0])
            .map(data => data.minutes);

          if (result.length) {
            return result[0].filter(minutes => minutes >= this.daysHours[dayIndex][0][0][1]);
          }

          return result;
        } else {
          return baseHours.filter(data => data.hour === this.daysHours[dayIndex][0][1][0])
            .map(data => data.minutes)[0];
        }
      }

      if (rangeIndex > 0 && colIndex === 0) {
        if (this.daysHours[dayIndex][rangeIndex][0][0] === '24') {
          return ['00'];
        }

        if (this.daysHours[dayIndex][rangeIndex - 1][1][0] === this.daysHours[dayIndex][rangeIndex][0][0]) {
          let result = baseHours.filter(data => data.hour === this.daysHours[dayIndex][rangeIndex - 1][1][0])
            .map(data => data.minutes);

          if (result.length) {
            return result[0].filter(minutes => minutes >= this.daysHours[dayIndex][rangeIndex - 1][1][1]);
          }

          return result;
        } else {
          return baseHours.filter(data => data.hour === this.daysHours[dayIndex][rangeIndex - 1][1][0])
            .map(data => data.minutes)[0];
        }
      }

      if (rangeIndex > 0 && colIndex === 1) {
        if (this.daysHours[dayIndex][rangeIndex][1][0] === '24') {
          return ['00'];
        }

        if (this.daysHours[dayIndex][rangeIndex][0][0] === this.daysHours[dayIndex][rangeIndex][1][0]) {
          let result = baseHours.filter(data => data.hour === this.daysHours[dayIndex][rangeIndex][1][0])
            .map(data => data.minutes);

          if (result.length) {
            return result[0].filter(minutes => minutes >= this.daysHours[dayIndex][rangeIndex][0][1]);
          }

          return result;
        } else {
          return baseHours.filter(data => data.hour === this.daysHours[dayIndex][rangeIndex][1][0])
            .map(data => data.minutes)[0];
        }
      }
    },

    /**
     * Prepares retrieved hours data to separated hours and minutes
     */
    prepareHours (newHours) {
      let hours = JSON.parse(JSON.stringify(newHours));

      if (!hours.length) {
        return [];
      }

      for (let day = 0; day < 7; day++) {
        for (let range = 0; range < hours[day].length; range++) {
          hours[day][range] = [
            hours[day][range][0].split(':'),
            hours[day][range][1].split(':')
          ];
        }
      }

      return hours;
    },
    /**
     * Fixes hours after changes
     */
    fixHours (newHours) {
      for (let day = 0; day < newHours.length; day++) {
        for (let range = 0; range < newHours[day].length; range++) {
          // Fixing problems in the first time range
          if (newHours[day][range][1][0] < newHours[day][range][0][0]) {
            newHours[day][range][1][0] = newHours[day][range][0][0];
          }

          if (
            newHours[day][range][0][0] === newHours[day][range][1][0] &&
            newHours[day][range][1][1] < newHours[day][range][0][1]
          ) {
            newHours[day][range][1][1] = newHours[day][range][0][1];
          }

          // Detecting hours problems in additional time ranges
          if (range > 0) {
            if (newHours[day][range][0][0] < newHours[day][range - 1][1][0]) {
              newHours[day][range][0][0] = newHours[day][range - 1][1][0];
            }

            if (
              newHours[day][range][0][0] === newHours[day][range - 1][1][0] &&
              newHours[day][range][0][1] < newHours[day][range - 1][1][1]
            ) {
              newHours[day][range][0][1] = newHours[day][range - 1][1][1];
            }

            if (newHours[day][range][1][0] < newHours[day][range][0][0]) {
              newHours[day][range][1][0] = newHours[day][range][0][0];
            }

            if (
              newHours[day][range][1][0] === newHours[day][range][0][0] &&
              newHours[day][range][1][1] < newHours[day][range][0][1]
            ) {
              newHours[day][range][1][1] = newHours[day][range][0][1];
            }
          }

          // Solving problems with "24" hour
          if (newHours[day][range][0][0] === '24' && newHours[day][range][0][1] !== '00') {
            newHours[day][range][0][1] = '00';
          }

          if (newHours[day][range][1][0] === '24' && newHours[day][range][1][1] !== '00') {
            newHours[day][range][1][1] = '00';
          }

          // Solving issue with minutes on the last hours
          if (this.useCompanyHours) {
            let lastIndex = this.fullDayHoursAndMinutes[day].length - 1;
            let lastMinutesIndex = this.fullDayHoursAndMinutes[day][lastIndex].minutes.length - 1;

            if (
              newHours[day][range][0][0] === this.fullDayHoursAndMinutes[day][lastIndex].hour &&
              newHours[day][range][0][1] > this.fullDayHoursAndMinutes[day][lastIndex].minutes[lastMinutesIndex]
            ) {
              newHours[day][range][0][1] = this.fullDayHoursAndMinutes[day][lastIndex].minutes[lastMinutesIndex];
            }

            if (
              newHours[day][range][1][0] === this.fullDayHoursAndMinutes[day][lastIndex].hour &&
              newHours[day][range][1][1] > this.fullDayHoursAndMinutes[day][lastIndex].minutes[lastMinutesIndex]
            ) {
              newHours[day][range][1][1] = this.fullDayHoursAndMinutes[day][lastIndex].minutes[lastMinutesIndex];
            }
          } else {
            let lastIndex = this.fullDayHoursAndMinutes.length - 1;
            let lastMinutesIndex = this.fullDayHoursAndMinutes[lastIndex].minutes.length - 1;

            if (
              newHours[day][range][0][0] === this.fullDayHoursAndMinutes[lastIndex].hour &&
              newHours[day][range][0][1] > this.fullDayHoursAndMinutes[lastIndex].minutes[lastMinutesIndex]
            ) {
              newHours[day][range][0][1] = this.fullDayHoursAndMinutes[lastIndex].minutes[lastMinutesIndex];
            }

            if (
              newHours[day][range][1][0] === this.fullDayHoursAndMinutes[lastIndex].hour &&
              newHours[day][range][1][1] > this.fullDayHoursAndMinutes[lastIndex].minutes[lastMinutesIndex]
            ) {
              newHours[day][range][1][1] = this.fullDayHoursAndMinutes[lastIndex].minutes[lastMinutesIndex];
            }
          }

          // Solving issue with minutes on the first hours
          if (this.useCompanyHours) {
            if (
              newHours[day][range][0][0] === this.fullDayHoursAndMinutes[day][0].hour &&
              newHours[day][range][0][1] < this.fullDayHoursAndMinutes[day][0].minutes[0]
            ) {
              newHours[day][range][0][1] = this.fullDayHoursAndMinutes[day][0].minutes[0];
            }

            if (
              newHours[day][range][1][0] === this.fullDayHoursAndMinutes[day][0].hour &&
              newHours[day][range][1][1] < this.fullDayHoursAndMinutes[day][0].minutes[0]
            ) {
              newHours[day][range][1][1] = this.fullDayHoursAndMinutes[day][0].minutes[0];
            }
          } else {
            if (
              newHours[day][range][0][0] === this.fullDayHoursAndMinutes[0].hour &&
              newHours[day][range][0][1] < this.fullDayHoursAndMinutes[0].minutes[0]
            ) {
              newHours[day][range][0][1] = this.fullDayHoursAndMinutes[0].minutes[0];
            }

            if (
              newHours[day][range][1][0] === this.fullDayHoursAndMinutes[0].hour &&
              newHours[day][range][1][1] < this.fullDayHoursAndMinutes[0].minutes[0]
            ) {
              newHours[day][range][1][1] = this.fullDayHoursAndMinutes[0].minutes[0];
            }
          }
        }
      }

      if (JSON.stringify(this.daysHours) !== JSON.stringify(newHours)) {
        this.daysHours = newHours.slice();
      }
    },
    /**
     * Returns hours data in a format with joined hours and minutes
     */
    getHours () {
      let newHours = JSON.parse(JSON.stringify(this.daysHours));

      if (!newHours.length) {
        return [];
      }

      for (let day = 0; day < 7; day++) {
        for (let range = 0; range < newHours[day].length; range++) {
          newHours[day][range] = [
            newHours[day][range][0].join(':'),
            newHours[day][range][1].join(':')
          ];
        }
      }

      return newHours;
    },
    additionalHours (dayIndex) {
      if (this.daysHours[dayIndex].length > 1) {
        return this.daysHours[dayIndex].slice(1);
      }

      return [];
    },
    addHours (index) {
      this.daysHours[index].push([
        [
          '00',
          '00'
        ],
        [
          '00',
          '00'
        ]
      ]);
    },
    removeHours (dayIndex, hoursIndex) {
      this.daysHours[dayIndex].splice(hoursIndex, 1);
    },
    refresh () {
      let newID = 'work-hours-' + new Date().getTime();
      this.componentIdForRefresh = newID;
    }
  },
  beforeDestroy () {
    this.$bus.$off('configuration-loaded', this.activateComponent);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.work-hours {
  width: 100%;

  .separator {
    color: $color-text-light;
    padding: 0 .5rem;
  }

  .field {
    align-items: flex-start;
    width: 100%;

    &.is-hidden {
      display: none;
    }

    .switcher {
      margin: 0;

      & + .label-closed {
        margin-left: 2rem;
      }
    }

    .week-day-hours {
      margin-left: 2rem;
      margin-top: -.6rem;
      width: 50%;

      select {
        width: 70px;
      }

      button {
        display: inline-block;
      }

      &-item {
        margin-top: .5rem;

        &:first-child {
          margin-top: 0;
        }
      }
    }

    button {
      position: relative;
      top: 2px;

      /deep/ svg {
        position: relative;
        top: 4px;
      }
    }
  }

  &-day {
    &.is-hidden {
      display: none;
    }
  }
}

@media (max-width: 960px) {
  .work-hours-day {
    label {
      width: 120px!important;
    }

    .week-day-hours {
      width: calc(100% - 180px)!important;
    }
  }
}

@media (max-width: 720px) {
  .work-hours-day {
    label {
      margin-bottom: 2.4rem;
      width: auto!important;
    }

    .week-day-hours {
      margin-left: 0!important;
      width: 100%!important;
    }
  }
}

@media (max-width: 460px) {
  .work-hours-day {
    select {
      background-position: calc(100% - 0.5rem) center, center center;
      padding-left: .5rem!important;
      padding-right: 1rem!important;
      width: 40px!important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/WorkHours.vue